import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSports } from 'containers/Mundial2026/context/SportsProvider';
import { useTranslation } from 'hooks/useTranslation';
import FullStatsSwitch from 'containers/Mundial2026/WCCountries/components/FullStatsSwitch';
import PlayerCard from './components/PlayerCard';
import FormationMap from './components/FormationMap';
import {
  SQUAD_EMPTY_LABEL,
  SQUAD_LAST_LINEUP_LABEL,
  SQUAD_MATCH_LABEL,
  SQUAD_SECTION_LABELS,
  SQUAD_TOGGLE_LABEL,
} from './constants';
import styles from './Squad.module.scss';

const Squad = ({ showFullSquad, onToggleChange }) => {
  const { countries } = useSports();
  const { translationMapper } = useTranslation();

  const sectionLabels = useMemo(() => translationMapper(SQUAD_SECTION_LABELS), [
    translationMapper,
  ]);
  const toggleLabel = useMemo(
    () => translationMapper(SQUAD_TOGGLE_LABEL).label,
    [translationMapper],
  );
  const emptyLabel = useMemo(() => translationMapper(SQUAD_EMPTY_LABEL).label, [
    translationMapper,
  ]);
  const lastLineupLabel = useMemo(
    () => translationMapper(SQUAD_LAST_LINEUP_LABEL).label,
    [translationMapper],
  );
  const matchLabel = useMemo(() => translationMapper(SQUAD_MATCH_LABEL).label, [
    translationMapper,
  ]);

  const data = countries?.data;
  const lineup = data?.lineups?.[0];
  const primaryColor = data?.teamCard?.team?.primaryHex;

  const formatCoachInfo = useCallback(coach => {
    if (!coach) return null;
    if (coach.displayInfo) return coach.displayInfo;
    if (coach.displayBirthDate && coach.age != null) {
      return `${coach.displayBirthDate} (${coach.age} años)`;
    }
    return coach.displayBirthDate || null;
  }, []);

  const formatMatchMeta = useCallback(match => {
    if (!match) return null;
    const { displayDate, displayTime, timeZoneLabel } = match;
    const timePart = [displayTime, timeZoneLabel].filter(Boolean).join(' / ');
    return [displayDate, timePart].filter(Boolean).join(' | ');
  }, []);

  if (!lineup) {
    return <div className={styles['squad']} />;
  }

  const match = lineup.lastLineup?.match;
  const matchDate = match?.date;
  const matchMeta = formatMatchMeta(match);
  const formation = match?.formation;
  const coach = lineup.coach;
  const coachDisplayInfo = formatCoachInfo(coach);

  const filterPlayers = players =>
    showFullSquad ? players : players.filter(p => p.isStarter);

  const renderPlayerCard = (player, opts = {}) => (
    <PlayerCard
      key={player.playerId || player.coachId || player.name}
      name={player.name}
      displayInfo={opts.displayInfoOverride || player.displayInfo}
      number={player.number ?? null}
      image={player.image}
      movements={player.movements || []}
      matchDate={matchDate}
      primaryColor={primaryColor}
    />
  );

  const playerSections = [
    { key: 'goalkeeper', players: filterPlayers(lineup.goalkeeper || []) },
    { key: 'defenders', players: filterPlayers(lineup.defenders || []) },
    { key: 'midfielders', players: filterPlayers(lineup.midfielders || []) },
    { key: 'forwards', players: filterPlayers(lineup.forwards || []) },
  ];

  return (
    <div className={styles['squad']}>
      <div className={styles['squad__layout']}>
        <div className={styles['squad__left']}>
          <header className={styles['squad__match-header']}>
            <h2 className={styles['squad__match-title']}>{lastLineupLabel}</h2>
            {matchMeta && (
              <p className={styles['squad__match-meta']}>
                <span className={styles['squad__match-meta-label']}>
                  {matchLabel}:{' '}
                </span>
                {matchMeta}
              </p>
            )}
          </header>

          <FormationMap
            goalkeeper={lineup.goalkeeper}
            defenders={lineup.defenders}
            midfielders={lineup.midfielders}
            forwards={lineup.forwards}
            formation={formation}
            primaryColor={primaryColor}
          />
        </div>

        <div className={styles['squad__right']}>
          <div className={styles['squad__toggle-row']}>
            <FullStatsSwitch
              checked={showFullSquad}
              onChange={onToggleChange}
              label={toggleLabel}
            />
          </div>

          {coach && (
            <section className={styles['squad__section']}>
              <h3 className={styles['squad__section-title']}>
                {sectionLabels.coach}
              </h3>
              <div
                className={classNames(
                  styles['squad__cards-grid'],
                  styles['squad__cards-grid--single'],
                )}
              >
                {renderPlayerCard(coach, {
                  displayInfoOverride: coachDisplayInfo,
                })}
              </div>
            </section>
          )}

          {playerSections.map(({ key, players }) => (
            <section key={key} className={styles['squad__section']}>
              <h3 className={styles['squad__section-title']}>
                {sectionLabels[key]}
              </h3>
              <div className={styles['squad__cards-grid']}>
                {players.map(player => renderPlayerCard(player))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

Squad.propTypes = {
  showFullSquad: PropTypes.bool,
  onToggleChange: PropTypes.func.isRequired,
};

Squad.defaultProps = {
  showFullSquad: false,
};

export default Squad;
